import React, { useState, useRef, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';

interface TitleCellProps {
  title: string;
  onTitleChange: (newTitle: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const TitleCell: React.FC<TitleCellProps> = ({
  title,
  onTitleChange,
  disabled = false,
  placeholder = 'Enter title...'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [hasChanges, setHasChanges] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update editValue when title prop changes
  useEffect(() => {
    setEditValue(title);
    setHasChanges(false);
  }, [title]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      const inputElement = inputRef.current.querySelector('input');
      if (inputElement) {
        inputElement.focus();
        inputElement.select();
      }
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setEditValue(newValue);
    setHasChanges(newValue !== title);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSave();
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  const handleSave = () => {
    if (hasChanges && editValue.trim() !== title) {
      onTitleChange(editValue.trim());
    }
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setEditValue(title);
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <TextField
        ref={inputRef}
        value={editValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        variant="outlined"
        size="small"
        multiline
        fullWidth
        placeholder={placeholder}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '14px',
            '& fieldset': {
              borderColor: '#2196f3',
            },
            '&:hover fieldset': {
              borderColor: '#1976d2',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2196f3',
            },
          },
        }}
      />
    );
  }

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px solid transparent',
        '&:hover': {
          backgroundColor: disabled ? 'transparent' : '#f5f5f5',
          borderColor: disabled ? 'transparent' : '#e0e0e0',
        },
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          color: title ? 'text.primary' : 'text.secondary',
          fontStyle: title ? 'normal' : 'italic',
        }}
        title={title || placeholder}
      >
        {title || placeholder}
      </Typography>
    </Box>
  );
};

export default TitleCell;
